import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Label,
} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';
import {z} from 'zod';

import {usePostServiceCaseXentryCampaignRejectMutation} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, PredefinedNotes} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

const schema = z.object({reason: z.string().min(10)});

type FormType = z.infer<typeof schema>;

interface RejectFormProps extends RequiredTestIdProps {
  onClose: () => void;
  serviceCaseId: string;
  serviceJobId: string;
}

export function RejectForm(props: RejectFormProps) {
  const [postServiceCaseXentryCampaignReject] = usePostServiceCaseXentryCampaignRejectMutation();

  const onSubmit: FormSubmitHandler<FormType> = (values) =>
    postServiceCaseXentryCampaignReject({
      serviceCaseId: props.serviceCaseId,
      serviceJobId: props.serviceJobId,
      body: {rejectionReason: values.reason},
    })
      .unwrap()
      .then(props.onClose)
      .catch(handleApiError);

  return (
    <Form<FormType> experimentalZodSchema={schema} onSubmit={onSubmit}>
      {(control, formApi) => (
        <VStack spacing={4}>
          <VStack>
            <HStack align="flex-end">
              <Box flex={1}>
                <Label isRequired>{i18n.t('general.labels.reason')}</Label>
              </Box>
              <PredefinedNotes
                note={formApi.watch('reason')}
                resource="SERVICE_JOB"
                context="reason-for-reject"
                onPrefill={(reason) => formApi.setValue('reason', reason)}
                isLinkVariant
                data-testid={suffixTestId('predefinedNotes', props)}
              />
            </HStack>
            <FormField control={control} name="reason" type="textarea" isRequired />
          </VStack>
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              title={i18n.t('general.actions.cancel')}
              onClick={() => props.onClose()}
            />
            <FormButton control={control} type="submit" title={i18n.t('general.actions.confirm')} />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
